import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Button,
  Typography,
  Card,
  CardContent,
  Stepper,
  Step,
  StepLabel,
  TextField,
  Grid,
  InputAdornment,
  Alert,
  Autocomplete,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import { ArrowBack, ArrowForward } from '@mui/icons-material';
import AdHierarchySidebar from '../../components/AdHierarchySidebar';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { campaignsService, CreateCampaignRequest } from '../../services/campaigns.service';
import { adAccountsService, AdAccount } from '../../services/ad-accounts.service';
import { createCampaignSchema } from '../../schemas/campaign.schema';
import { errorMessage } from '../../utils/errorMessage';

const steps = ['Campaign Details', 'Budget', 'Review'];

const objectives = [
  { value: 'AWARENESS', label: 'Awareness' },
  { value: 'TRAFFIC', label: 'Traffic' },
  { value: 'ENGAGEMENT', label: 'Engagement' },
  { value: 'LEADS', label: 'Leads' },
  { value: 'SALES', label: 'Sales' },
];

// '' = None, sent as []. Facebook fixes the category at creation, so the field is create-only.
const specialCategories = [
  { value: '', label: 'None' },
  { value: 'HOUSING', label: 'Housing' },
  { value: 'EMPLOYMENT', label: 'Employment' },
  { value: 'CREDIT', label: 'Credit' },
  { value: 'ISSUES_ELECTIONS_POLITICS', label: 'Social Issues, Elections or Politics' },
  { value: 'FINANCIAL_PRODUCTS_SERVICES', label: 'Financial Products & Services' },
  { value: 'ONLINE_GAMBLING_AND_GAMING', label: 'Online Gambling & Gaming' },
];
const categoryLabel = (cats?: string[]) =>
  specialCategories.find((c) => c.value === (cats?.[0] || ''))?.label ?? 'None';

export default function CreateCampaignPage() {
  const navigate = useNavigate();
  const { id: editId } = useParams<{ id: string }>();
  const isEditMode = !!editId;
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const [activeStep, setActiveStep] = useState(0);
  const [budgetStrategy, setBudgetStrategy] = useState<'adset' | 'campaign'>('adset');

  const { data: adAccountsResult, isLoading: isLoadingAccounts } = useQuery({
    queryKey: ['adAccounts'],
    queryFn: () => adAccountsService.query(),
  });

  const adAccounts: AdAccount[] = adAccountsResult?.data ?? [];

  const {
    control,
    handleSubmit,
    watch,
    setValue,
    trigger,
    formState: { errors },
  } = useForm<CreateCampaignRequest>({
    resolver: yupResolver(createCampaignSchema),
    defaultValues: {
      ad_account_id: '',
      name: '',
      objective: 'AWARENESS',
      status: 'PAUSED',
      special_ad_categories: [],
    },
  });

  // Fetch + populate existing campaign in edit mode
  const { data: existingCampaign } = useQuery({
    queryKey: ['campaign', editId],
    queryFn: () => campaignsService.getById(editId!),
    enabled: isEditMode,
  });

  const [initialized, setInitialized] = useState<string | false>(false);
  useEffect(() => {
    if (!existingCampaign || initialized === editId) return;
    setValue('ad_account_id', existingCampaign.ad_account_id);
    setValue('name', existingCampaign.name);
    setValue('objective', existingCampaign.objective);
    setValue('special_ad_categories', existingCampaign.special_ad_categories ?? []);
    setValue('status', existingCampaign.status as any);
    if (existingCampaign.daily_budget) {
      setBudgetStrategy('campaign');
      setValue('daily_budget', Number(existingCampaign.daily_budget) as any);
    }
    if (existingCampaign.lifetime_budget) {
      setBudgetStrategy('campaign');
      setValue('lifetime_budget', Number(existingCampaign.lifetime_budget) as any);
    }
    setInitialized(editId || false);
  }, [existingCampaign]); // eslint-disable-line react-hooks/exhaustive-deps

  const createMutation = useMutation({
    mutationFn: (payload: any) =>
      isEditMode ? campaignsService.update(editId!, payload) : campaignsService.create(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['campaigns'] });
      enqueueSnackbar(isEditMode ? 'Campaign saved' : 'Campaign created', { variant: 'success' });
      if (!isEditMode) navigate('/campaigns');
    },
    onError: (err: unknown) => {
      enqueueSnackbar(errorMessage(err, isEditMode ? 'Failed to save campaign' : 'Failed to create campaign'), { variant: 'error' });
    },
  });

  const formData = watch();

  const onSubmit = (data: CreateCampaignRequest) => {
    // Not the form's to set: created paused, changed afterwards by the play/pause control.
    const { status: _status, objective, special_ad_categories, ...rest } = data;
    createMutation.mutate({
      ...rest,
      // Both fixed at creation; resending on an edit fails with an error nobody asked for.
      ...(isEditMode ? {} : { objective, special_ad_categories: special_ad_categories ?? [] }),
      daily_budget: data.daily_budget ? Number(data.daily_budget) : undefined,
      lifetime_budget: data.lifetime_budget ? Number(data.lifetime_budget) : undefined,
    });
  };

  // Validate each step's own fields before advancing: otherwise an invalid field (e.g. both
  // budgets set) blocks the final submit silently, on a step where it isn't even visible.
  const stepFields: (keyof CreateCampaignRequest)[][] = [
    ['ad_account_id', 'name', 'objective', 'special_ad_categories'],
    ['daily_budget', 'lifetime_budget'],
    [],
  ];

  const handleNext = async () => {
    if (activeStep === steps.length - 1) {
      handleSubmit(onSubmit)();
      return;
    }
    if (await trigger(stepFields[activeStep])) setActiveStep((prev) => prev + 1);
  };

  const handleBack = () => {
    setActiveStep((prev) => prev - 1);
  };

  return (
    <Box sx={{ display: 'flex', gap: 0, mx: -3, mt: -3, minHeight: 'calc(100vh - 120px)' }}>
      {isEditMode && (
        <AdHierarchySidebar campaignId={editId} />
      )}

      <Box sx={{ flex: 1, p: 3, overflow: 'auto', maxWidth: 900 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <Button startIcon={<ArrowBack />} onClick={() => navigate('/campaigns')}>
            Back
          </Button>
          <Typography variant="h4" fontWeight={600} sx={{ ml: 2 }}>
            {isEditMode ? 'Edit Campaign' : 'Create Campaign'}
          </Typography>
        </Box>

      <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
        {steps.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      <Card>
        <CardContent>
          {activeStep === 0 && (
            <Grid container spacing={3}>
              {adAccounts.length === 0 && !isLoadingAccounts && (
                <Grid size={{ xs: 12 }}>
                  <Alert severity="warning">
                    No ad accounts connected. Please connect an ad account first.
                  </Alert>
                </Grid>
              )}
              <Grid size={{ xs: 12 }}>
                <Controller
                  name="ad_account_id"
                  control={control}
                  render={({ field }) => (
                    <Autocomplete
                      options={adAccounts}
                      getOptionLabel={(o) => `${o.name} (${o.fb_account_id})`}
                      value={adAccounts.find(a => a.id === field.value) || null}
                      onChange={(_, v) => field.onChange(v?.id || '')}
                      // Moving it here would only move it in Neptune.
                      disabled={isLoadingAccounts || isEditMode}
                      loading={isLoadingAccounts}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Ad Account"
                          error={!!errors.ad_account_id}
                          helperText={errors.ad_account_id?.message}
                        />
                      )}
                    />
                  )}
                />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <Controller
                  name="name"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      label="Campaign Name"
                      error={!!errors.name}
                      helperText={errors.name?.message}
                    />
                  )}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <Controller
                  name="objective"
                  control={control}
                  render={({ field }) => (
                    <Autocomplete
                      options={objectives}
                      getOptionLabel={(o) => o.label}
                      value={objectives.find(o => o.value === field.value) || null}
                      onChange={(_, v) => field.onChange(v?.value || '')}
                      renderInput={(params) => <TextField {...params} label="Objective" />}
                    />
                  )}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <Controller
                  name="special_ad_categories"
                  control={control}
                  render={({ field }) => {
                    const current = (field.value && field.value[0]) || '';
                    return (
                      <Autocomplete
                        options={specialCategories}
                        getOptionLabel={(o) => o.label}
                        value={specialCategories.find((o) => o.value === current) || specialCategories[0]}
                        onChange={(_, v) => field.onChange(v && v.value ? [v.value] : [])}
                        disabled={isEditMode}
                        disableClearable
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Special Ad Category"
                            helperText={isEditMode
                              ? 'Fixed at creation — cannot be changed'
                              : 'Required by Facebook for housing, employment, credit, politics, etc.'}
                          />
                        )}
                      />
                    );
                  }}
                />
              </Grid>
            </Grid>
          )}

          {activeStep === 1 && (
            <Grid container spacing={3}>
              <Grid size={{ xs: 12 }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Budget Strategy
                </Typography>
                <ToggleButtonGroup
                  value={budgetStrategy}
                  exclusive
                  onChange={(_, value) => value && setBudgetStrategy(value)}
                  fullWidth
                  sx={{ mb: 2 }}
                >
                  <ToggleButton value="adset">
                    Ad Set Budget
                  </ToggleButton>
                  <ToggleButton value="campaign">
                    Campaign Budget (CBO)
                  </ToggleButton>
                </ToggleButtonGroup>
                <Typography variant="body2" color="text.secondary">
                  {budgetStrategy === 'adset'
                    ? 'Budgets will be set individually on each ad set.'
                    : 'Facebook will automatically distribute your budget across ad sets for best results.'}
                </Typography>
              </Grid>

              {budgetStrategy === 'campaign' && (
                <>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <Controller
                      name="daily_budget"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          fullWidth
                          label="Daily Budget"
                          type="number"
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">$</InputAdornment>
                            ),
                          }}
                          error={!!errors.daily_budget}
                          helperText={errors.daily_budget?.message || 'Budget spent per day across all ad sets'}
                        />
                      )}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <Controller
                      name="lifetime_budget"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          fullWidth
                          label="Lifetime Budget"
                          type="number"
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">$</InputAdornment>
                            ),
                          }}
                          error={!!errors.lifetime_budget}
                          helperText={errors.lifetime_budget?.message || 'Total budget for the campaign lifetime'}
                        />
                      )}
                    />
                  </Grid>
                  <Grid size={{ xs: 12 }}>
                    <Alert severity="info">
                      Choose either Daily Budget or Lifetime Budget, not both.
                    </Alert>
                  </Grid>
                </>
              )}
            </Grid>
          )}

          {activeStep === 2 && (
            <Box>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Review Your Campaign
              </Typography>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Typography variant="body2" color="text.secondary">
                    Ad Account
                  </Typography>
                  <Typography variant="body1">
                    {adAccounts.find((a) => a.id === formData.ad_account_id)?.name || '-'}
                  </Typography>
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Typography variant="body2" color="text.secondary">
                    Name
                  </Typography>
                  <Typography variant="body1">{formData.name}</Typography>
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Typography variant="body2" color="text.secondary">
                    Objective
                  </Typography>
                  <Typography variant="body1">{formData.objective}</Typography>
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Typography variant="body2" color="text.secondary">
                    Special Ad Category
                  </Typography>
                  <Typography variant="body1">{categoryLabel(formData.special_ad_categories)}</Typography>
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Typography variant="body2" color="text.secondary">
                    Status
                  </Typography>
                  <Typography variant="body1">{formData.status}</Typography>
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Typography variant="body2" color="text.secondary">
                    Budget Strategy
                  </Typography>
                  <Typography variant="body1">
                    {budgetStrategy === 'campaign' ? 'Campaign Budget (CBO)' : 'Ad Set Budget'}
                  </Typography>
                </Grid>
                {budgetStrategy === 'campaign' && (
                  <>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <Typography variant="body2" color="text.secondary">
                        Daily Budget
                      </Typography>
                      <Typography variant="body1">
                        {formData.daily_budget ? `$${formData.daily_budget}` : '-'}
                      </Typography>
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <Typography variant="body2" color="text.secondary">
                        Lifetime Budget
                      </Typography>
                      <Typography variant="body1">
                        {formData.lifetime_budget ? `$${formData.lifetime_budget}` : '-'}
                      </Typography>
                    </Grid>
                  </>
                )}
              </Grid>
            </Box>
          )}

          {createMutation.isError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {errorMessage(createMutation.error, isEditMode ? 'Failed to save campaign' : 'Failed to create campaign')}
            </Alert>
          )}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
            <Button disabled={activeStep === 0} onClick={handleBack}>
              Back
            </Button>
            <Button
              variant="contained"
              endIcon={activeStep === steps.length - 1 ? null : <ArrowForward />}
              onClick={handleNext}
              disabled={createMutation.isPending}
            >
              {activeStep === steps.length - 1 ? (isEditMode ? 'Save Changes' : 'Create Campaign') : 'Next'}
            </Button>
          </Box>
        </CardContent>
      </Card>
      </Box>
    </Box>
  );
}
